
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { DebtItem, MonthData, PayoffMethod } from './debtCalculatorTypes';
import { formatCurrency } from './debtCalculatorUtils';

interface DebtResultsProps {
  debts: DebtItem[];
  totalDebt: number;
  payoffPlan: MonthData[];
  totalInterest: number;
  payoffTime: number;
  monthlyPayment: number;
  payoffMethod: PayoffMethod;
}

const DebtResults: React.FC<DebtResultsProps> = ({
  debts,
  totalDebt,
  payoffPlan,
  totalInterest,
  payoffTime,
  monthlyPayment,
  payoffMethod
}) => {
  return (
    <div className="grid gap-6">
      <Card className="border-2 border-primary/20">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Debt Payoff Summary</h3>
          
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <div className="text-sm text-muted-foreground">Total Debt</div>
              <div className="text-lg font-medium">{formatCurrency(totalDebt)}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Time to Debt-Free</div>
              <div className="text-lg font-bold text-primary">
                {Math.floor(payoffTime / 12)} years, {payoffTime % 12} months
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Total Interest Paid</div>
              <div className="text-lg font-medium">{formatCurrency(totalInterest)}</div>
            </div>
          </div>
          
          <div className="mt-6">
            <div className="text-sm text-muted-foreground mb-1">Monthly Payment</div>
            <div className="text-xl font-bold">{formatCurrency(monthlyPayment)}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {payoffMethod === 'avalanche' ? 'Debt Avalanche Method' : 'Debt Snowball Method'}
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="space-y-2">
        <h4 className="font-medium">Payoff Schedule</h4>
        <div className="text-sm text-muted-foreground mb-2">
          Here's how your debts will be paid off over time:
        </div>
        
        <div className="rounded-md border overflow-hidden">
          <div className="overflow-x-auto max-h-64">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="p-2 text-left font-medium">Month</th>
                  <th className="p-2 text-left font-medium">Remaining Debts</th>
                  <th className="p-2 text-left font-medium">Total Balance</th>
                  <th className="p-2 text-left font-medium">Interest Paid</th>
                </tr>
              </thead>
              <tbody>
                {payoffPlan.slice(0, 12).map((month) => {
                  const totalBalance = month.debts.reduce(
                    (sum, debt) => sum + debt.remainingBalance, 
                    0
                  );
                  const totalInterestForMonth = month.debts.reduce(
                    (sum, debt) => sum + debt.interest, 
                    0
                  );
                  
                  return (
                    <tr key={month.month} className="border-t">
                      <td className="p-2">{month.month}</td>
                      <td className="p-2">{month.debts.filter((d) => d.remainingBalance > 0).length}</td>
                      <td className="p-2">{formatCurrency(totalBalance)}</td>
                      <td className="p-2">{formatCurrency(totalInterestForMonth)}</td>
                    </tr>
                  );
                })}
                {payoffPlan.length > 12 && (
                  <tr className="border-t">
                    <td colSpan={4} className="p-2 text-center text-muted-foreground">
                      Showing first 12 months of {payoffPlan.length} total months
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <div className="space-y-4">
        <h4 className="font-medium">Debt Payoff Order</h4>
        <div className="space-y-2">
          {payoffMethod === 'avalanche' ? (
            <div className="text-sm">
              <strong>Debt Avalanche Method:</strong> Debts are paid off in order of highest interest rate first, 
              which minimizes the total interest paid.
            </div>
          ) : (
            <div className="text-sm">
              <strong>Debt Snowball Method:</strong> Debts are paid off in order of smallest balance first, 
              which provides psychological wins through early debt elimination.
            </div>
          )}
          
          <ol className="list-decimal list-inside space-y-1 text-sm ml-2">
            {debts
              .slice()
              .sort((a, b) => 
                payoffMethod === 'avalanche' 
                  ? b.interestRate - a.interestRate
                  : a.balance - b.balance
              )
              .map((debt, index) => (
                <li key={debt.id}>
                  {debt.name} - {formatCurrency(debt.balance)} at {debt.interestRate}%
                </li>
              ))
            }
          </ol>
        </div>
      </div>
    </div>
  );
};

export default DebtResults;
